import { memo, useState, useEffect } from 'react';
import {
  ModalBody,
  ModalContent,
  Modal,
  ModalHeader,
  ModalFooter,
  Select,
  SelectItem,
  Textarea,
} from '@heroui/react';
import { format } from 'date-fns';
import cn from 'classnames';
import {
  useUpdateAttendanceMutation,
  useCreateAttendanceMutation,
} from '@/entities/TeacherAttendance';
import type { ITeacherModuleAttendanceStudent, ITeacherModuleAttendanceLesson } from '@/entities/TeacherAttendance';
import { AppButton } from '@/shared/ui/AppButton';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './ChangeAttendanceModal.module.scss';

const ATTENDANCE_OPTIONS = [
  { value: 1 as const, label: '1.0', color: 'bg-[#CFF7D3] text-black' },
  { value: 0.5 as const, label: '0.5', color: 'bg-[#FFF9C4] text-black' },
  { value: 0 as const, label: '0.0', color: 'bg-[#FDD3D0] text-black' },
];

interface ChangeAttendanceModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  student: ITeacherModuleAttendanceStudent;
  lesson: ITeacherModuleAttendanceLesson;
  currentValue: number | null;
  onSuccess?: () => void;
}

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  if (!data || typeof data !== 'object') return undefined;
  const message = 'message' in data ? (data as { message?: unknown }).message : undefined;
  return typeof message === 'string' ? message : undefined;
};

export const ChangeAttendanceModal = memo(
  ({
    isOpen,
    onOpenChange,
    student,
    lesson,
    currentValue,
    onSuccess,
  }: ChangeAttendanceModalProps) => {
    const { toast } = useToast();
    const [value, setValue] = useState<string>(String(currentValue ?? 0));
    const [comment, setComment] = useState('');
    const [updateAttendance, { isLoading: isUpdating }] = useUpdateAttendanceMutation();
    const [createAttendance, { isLoading: isCreating }] = useCreateAttendanceMutation();
    const isLoading = isUpdating || isCreating;

    useEffect(() => {
      if (isOpen) {
        setValue(String(currentValue ?? 0));
        setComment('');
      }
    }, [isOpen, currentValue]);

    const studentName =
      student.firstName && student.lastName
        ? `${student.firstName} ${student.lastName}`
        : student.username;
    const studentId = student.courseIdentifier ?? `ID${student.enrollmentId}`;
    const dateTime = lesson.scheduledAt
      ? (() => {
          try {
            const d = new Date(lesson.scheduledAt);
            return { date: format(d, 'dd.MM.yyyy'), time: format(d, 'HH:mm') };
          } catch {
            return { date: '—', time: '' };
          }
        })()
      : { date: '—', time: '' };

    const numValue = Number(value) as 0 | 0.5 | 1;
    const valueChanged = numValue !== (currentValue ?? null);
    const hasExistingRecord = currentValue !== null;
    const commentRequired = hasExistingRecord;
    const isValid = commentRequired ? comment.trim().length > 0 : true;

    const handleSubmit = async () => {
      if (commentRequired && !comment.trim()) return;
      if (hasExistingRecord && !valueChanged) return;
      try {
        if (hasExistingRecord) {
          await updateAttendance({
            groupLessonId: lesson.groupLessonId,
            enrollmentId: student.enrollmentId,
            value: numValue,
            comment: comment.trim(),
          }).unwrap();
        } else {
          await createAttendance({
            groupLessonId: lesson.groupLessonId,
            enrollmentId: student.enrollmentId,
            value: numValue,
          }).unwrap();
        }
        toast({ title: 'Success', description: 'Attendance updated', color: 'success' });
        onSuccess?.();
        onOpenChange(false);
      } catch (err) {
        toast({
          title: 'Error',
          description: getApiErrorMessage(err) || 'Failed to update attendance',
          color: 'danger',
        });
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="center"
        motionProps={scaleInOut}
        size="md"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[520px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className={styles.title}>
                Change Attendance Record
              </ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <div className={styles.studentInfo}>
                  <span className="text-black-headline font-medium">{studentId}</span>
                  <span className="flex items-center gap-2 text-black-headline">
                    <span className="w-6 h-6 rounded-full bg-[#F9FAFB] flex items-center justify-center text-grey-secondary shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                    </span>
                    {studentName}
                  </span>
                  <div>
                    <Select
                      size="sm"
                      selectedKeys={[value]}
                      onSelectionChange={(keys) =>
                      setValue(Array.from(keys)[0] as string)
                    }
                      classNames={{
                      trigger: 'w-20 min-h-9 rounded-lg',
                      value: 'font-medium',
                    }}
                      renderValue={(items) => {
                      const v = items[0]?.key as string;
                      const opt = ATTENDANCE_OPTIONS.find((o) => String(o.value) === v);
                      return (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded ${opt?.color ?? ''}`}
                        >
                          {Number(v).toFixed(1)}
                        </span>
                      );
                    }}
                  >
                      {ATTENDANCE_OPTIONS.map((opt) => (
                        <SelectItem
                          key={String(opt.value)}
                          textValue={opt.label}
                          classNames={{ base: 'data-[hover=true]:bg-grey-10' }}
                      >
                          <span className={`inline-flex px-3 py-1 rounded ${opt.color}`}>
                            {opt.label}
                          </span>
                        </SelectItem>
                    ))}
                    </Select>
                  </div>
                  <span className="text-grey-secondary text-sm ml-auto">
                    <span className="block">{dateTime.date}</span>
                    <span>{dateTime.time}</span>
                  </span>
                </div>
                <p className="text-sm text-grey-secondary">
                  Any attendance update within 24 hours requires a short comment
                </p>
                <Textarea
                  label="Comment"
                  labelPlacement="outside"
                  placeholder="Add a reason for this attendance change"
                  value={comment}
                  onValueChange={setComment}
                  minRows={3}
                  classNames={{
                    label: cn('text-black-headline font-medium', commentRequired && styles.requiredLabel),
                    input: 'min-h-[80px] rounded-lg ',
                    inputWrapper: 'rounded-lg border border-[#D9D9D9] bg-[#FFFFFF]',
                  }}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton
                  fullWidth
                  color="white"
                  className="border border-red-tertiary text-red-tertiary"
                  onPress={onClose}
                >
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  onPress={handleSubmit}
                  isLoading={isLoading}
                  disabled={
                    (commentRequired && !comment.trim()) ||
                    (hasExistingRecord && !valueChanged)
                  }
                >
                  Save changes
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

ChangeAttendanceModal.displayName = 'ChangeAttendanceModal';
